import {useEffect, useState} from "react";

/**
 * Read is_use_wake_word flag from environment settings injected on bootstrap.
 * Defaults to true if missing or unreadable to preserve legacy behavior.
 */
export function getIsUseWakeWordEnv() {
    try {
        const flag = window.__VOICE_SEARCH_ENV_SETTINGS__?.is_use_wake_word;
        return (typeof flag === 'boolean') ? flag : true;
    } catch (_) {
        return true;
    }
}

/**
 * Compute whether background wake word should be enabled based on
 * the environment flag and microphone permission status.
 */
export function computeShouldEnableWakeWord(isUseWakeWordEnv, micGranted) {
    return !!(isUseWakeWordEnv || micGranted);
}

/**
 * Hook that determines (non-invasively) whether microphone permission is already granted.
 * It uses the Permissions API when available and falls back to enumerateDevices heuristics.
 *
 * Optionally accepts a dependency (e.g., modal open state) to re-check permission
 * when it might have changed.
 */
export function useMicPermission(recheckDep) {
    const [micGranted, setMicGranted] = useState(false);

    useEffect(() => {
        let permStatus = null;
        let cancelled = false;

        const updateFromPermissions = (state) => {
            if (cancelled) return;
            setMicGranted(state === 'granted');
        };

        const tryPermissionsAPI = async () => {
            try {
                if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
                    const status = await navigator.permissions.query({name: 'microphone'});
                    permStatus = status;
                    updateFromPermissions(status.state);
                    if (status && 'onchange' in status) {
                        status.onchange = () => updateFromPermissions(status.state);
                    }
                    return true;
                }
            } catch (_) {
            }
            return false;
        };

        const fallbackEnumerate = async () => {
            try {
                if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const hasLabelledMic = devices.some(d => d.kind === 'audioinput' && d.label && d.label.trim() !== '');
                    updateFromPermissions(hasLabelledMic ? 'granted' : 'prompt');
                }
            } catch (_) {
            }
        };

        (async () => {
            const ok = await tryPermissionsAPI();
            if (!ok) await fallbackEnumerate();
        })();

        return () => {
            cancelled = true;
            try {
                if (permStatus) permStatus.onchange = null;
            } catch (_) {
            }
        };
    }, []);

    useEffect(() => {
        if (typeof recheckDep === 'undefined') return;
        let cancelled = false;
        const recheck = async () => {
            try {
                if (typeof navigator !== 'undefined' && navigator.permissions && navigator.permissions.query) {
                    const status = await navigator.permissions.query({name: 'microphone'});
                    if (cancelled) return;
                    if (status?.state === 'granted') setMicGranted(true);
                } else if (typeof navigator !== 'undefined' && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    if (cancelled) return;
                    const hasLabelledMic = devices.some(d => d.kind === 'audioinput' && d.label && d.label.trim() !== '');
                    if (hasLabelledMic) setMicGranted(true);
                }
            } catch (_) {
            }
        };
        recheck();
        return () => { cancelled = true; };
    }, [recheckDep]);

    return micGranted;
}
